// Ported from src/components/DeviceControls/DeviceControls.js on 2026-10-05.
import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import LegacyAudioPreview from 'components/UI/AudioPreview';
import type { DeviceAudioPreviewComponent } from 'features/Integrations/types/deviceControl.types';
import { Button } from 'react-bootstrap';
import { PiPencilLineFill } from 'react-icons/pi';
import type { DeviceAudioSelectorProps } from 'features/Integrations/types/deviceControl.types';

const AudioPreview = LegacyAudioPreview as DeviceAudioPreviewComponent;

const AUDIO_IDLE_CAVEAT =
  'Audio selection can be previewed, but it will not activate while the device is set to Idle.';

export default function DeviceAudioSelector({
  device,
  devices,
  audioOverride,
  on,
  audioManagerButtonEnabled,
  compact,
  audioList,
  onEverySiren,
  audioIdleCaveatId,
  canManageAudio,
  setData,
  stopLiveAudio,
}: DeviceAudioSelectorProps) {
  const updateSelectedAudio = (optionValue: number | null) => {
    const value = optionValue == null ? -1 : Number(optionValue);
    const list = audioOverride ? [audioOverride, ...(audioList || [])] : audioList || [];

    if (value === -1) {
      setData({
        ...device,
        audioIndex: -1,
        audio: null,
      });
    } else {
      const selectedAudio = list?.[value] || null;

      setData({
        ...device,
        audioIndex: value,
        audio: selectedAudio,
      });
    }
  };
  const renderAudioDropdown = () => {
    const list = audioOverride ? [audioOverride, ...(audioList || [])] : audioList || [];
    const hasAudioOptions = list.length > 0;
    const audioDropdownInactive = !device?.status;
    const noSelectionLabel = hasAudioOptions
      ? '-- Select Audio --'
      : devices
        ? '-- No audio files in the library yet --'
        : '-- No Audio Available --';
    // Index is the value, matching the audioIndex the parent stores; -1 means "nothing picked".
    const audioOptions = [
      { value: -1, label: noSelectionLabel },
      ...list.map((audio, index) => ({
        value: index,
        label: `${audio?.displayName || audio?.name || `Audio ${index + 1}`}${
          devices && audio?.id != null && !onEverySiren.has(audio.id)
            ? ' — will be added to the sirens that lack it'
            : ''
        }`,
      })),
    ];

    return (
      <div>
        <div className={`d-flex flex-column ${compact ? '' : 'flex-lg-row'} gap-2`}>
          {/* react-select takes no `title`, so the wrapper carries the idle tooltip for the
              mouse; the select points at the hidden copy above so it is announced on focus. */}
          <div
            className="flex-grow-1"
            title={audioDropdownInactive ? AUDIO_IDLE_CAVEAT : undefined}
          >
            {audioDropdownInactive && (
              <span className="visually-hidden" id={audioIdleCaveatId}>
                {AUDIO_IDLE_CAVEAT}
              </span>
            )}
            <BaseSelect
              isSearchable
              describedById={audioDropdownInactive ? audioIdleCaveatId : undefined}
              styles={
                audioDropdownInactive
                  ? {
                      control: (base) => ({
                        ...base,
                        backgroundColor: '#6c757d',
                        borderColor: '#ffffff',
                        color: '#ced4da',
                        opacity: 1,
                      }),
                    }
                  : undefined
              }
              selectProps={{
                'aria-label': 'Select Audio',
                options: audioOptions,
                value: findSelectOption(audioOptions, device?.audioIndex ?? -1),
                isDisabled: !hasAudioOptions,
                placeholder: noSelectionLabel,
                onChange: (option) => updateSelectedAudio(option?.value),
              }}
            />
          </div>

          {/* TODO: Re write AudioPreview component */}
          {device?.audio?.id && <AudioPreview tone={device?.audio} />}

          {audioManagerButtonEnabled && canManageAudio && device?.camera && (
            <Button
              className="p-2 px-4"
              aria-label="Manage audio files"
              onClick={() => {
                on('audio-manager', {});
                stopLiveAudio();
              }}
            >
              <PiPencilLineFill />
            </Button>
          )}
        </div>

        <hr className="d-block d-lg-none" />
      </div>
    );
  };
  return renderAudioDropdown();
}
